/**
 * InfinitSales - Facebook Messenger Service
 *
 * Cliente da API do canal Messenger. Espelha o instagramService, com PSID no
 * lugar do IGSID e a Pagina do Facebook no lugar da conta profissional.
 *
 * Diferencas de contrato que o front precisa respeitar:
 * - Nao existe "iniciar conversa": o cliente precisa mandar a primeira mensagem
 *   (limitacao da Meta, nao escolha nossa). Por isso nao ha ensureChat.
 * - Nao existe template nem envio fora da janela de 24h: o backend recusa com
 *   code 'outside_window' e o front mostra o aviso no lugar do composer.
 * - O OAuth tem DOIS passos (diferenca vs Instagram): connect-oauth troca o
 *   code e devolve a lista de Paginas do usuario; select-page finaliza a conta
 *   com a Pagina escolhida. Tokens nunca chegam ao frontend.
 * - Midia aceita documento (media_type 'file'), que o Instagram nao aceita.
 */

import api from '../global/api';
import requestCache from '../global/requestCache';

const MESSENGER_BASE = '/api/messenger';

// Status da conexao: o shell do inbox E o NavSidebar (remontado a cada troca
// de rota) pediam o mesmo GET. Cache 60s + dedup em voo; o 404 (flag off ou
// sem Pagina) e o caminho normal e fica cacheado tambem, relancado a quem
// chama para manter o contrato (quem chama trata o 404). As mutacoes de conta
// abaixo invalidam a chave.
const STATUS_CACHE_KEY = 'messenger:status';
const STATUS_TTL_MS = 60_000;

class MessengerService {
  // ============================================
  // CONEXAO
  // ============================================

  async getStatus({ force = false } = {}) {
    const entry = await requestCache.cachedGet(
      STATUS_CACHE_KEY,
      async () => {
        try {
          const response = await api.get(`${MESSENGER_BASE}/status`);
          return { data: response.data };
        } catch (e) {
          if (e?.response?.status === 404) return { notFound: e };
          throw e;
        }
      },
      { ttl: STATUS_TTL_MS, force },
    );
    if (entry.notFound) throw entry.notFound;
    return entry.data;
  }

  async getAccount() {
    const response = await api.get(`${MESSENGER_BASE}/account`);
    return response.data;
  }

  /**
   * Conecta a Pagina no modo TESTE, com um Page Access Token gerado no painel
   * da Meta. O backend descobre page_id/nome chamando /me com o proprio token.
   */
  async connectManual(accessToken, tokenExpiresAt = null) {
    const response = await api.post(`${MESSENGER_BASE}/account/connect-manual`, {
      access_token: accessToken,
      token_expires_at: tokenExpiresAt,
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  /**
   * Passo 1 do Facebook Login (OAuth de producao): troca o ?code= que a Meta
   * devolveu no redirect. O redirect_uri PRECISA ser o mesmo do authorize.
   * Devolve {data: {pages: [{page_id, page_name, profile_picture_url}]}} —
   * a conta so e criada no selectPage.
   */
  async connectOAuth(code, redirectUri) {
    const response = await api.post(`${MESSENGER_BASE}/account/connect-oauth`, {
      code,
      redirect_uri: redirectUri,
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  /**
   * Passo 2 do Facebook Login: finaliza a conta com a Pagina escolhida da
   * lista pendente do connectOAuth (o pendente expira em ~15 minutos).
   */
  async selectPage(pageId) {
    const response = await api.post(`${MESSENGER_BASE}/account/select-page`, {
      page_id: pageId,
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  /** purge=true apaga o historico junto — irreversivel. */
  async disconnect(purge = false) {
    const response = await api.delete(`${MESSENGER_BASE}/account`, {
      params: { purge },
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  // ============================================
  // CONVERSAS
  // ============================================

  async getChats(limit = 50, offset = 0, extra = {}) {
    const params = { limit, offset };
    if (extra.since) params.since = extra.since;
    if (extra.created_from) params.created_from = extra.created_from;
    if (extra.created_to) params.created_to = extra.created_to;
    if (extra.unanswered) params.unanswered = true;
    // Paridade D11 e D12: etapa e responsável filtrados no servidor.
    if (Array.isArray(extra.label_ids) && extra.label_ids.length) params.label_ids = extra.label_ids;
    if (extra.member_id) params.member_id = extra.member_id;
    // Busca DENTRO dos filtros (inbox_search_within_filters).
    if (extra.q) params.q = extra.q;
    if (extra.q && extra.search_messages) params.search_messages = true;
    // Repetível: ?cf=campo:valor. Formato de fio do motor core
    // (services/custom_field_search.py), igual nos 4 canais.
    if (Array.isArray(extra.custom_fields) && extra.custom_fields.length) {
      params.cf = extra.custom_fields
        .filter(({ field_id, value }) => field_id && value)
        .map(({ field_id, value }) => `${field_id}:${value}`);
    }
    const response = await api.get(`${MESSENGER_BASE}/chats`, { params, paramsSerializer: { indexes: null } });
    return response.data;
  }

  async searchChats(q, searchMessages = false, customFields = null) {
    const params = { q, search_messages: searchMessages };
    const cf = (customFields || [])
      .filter(({ field_id, value }) => field_id && value)
      .map(({ field_id, value }) => `${field_id}:${value}`);
    if (cf.length) params.cf = cf;
    const response = await api.get(`${MESSENGER_BASE}/chats/search`, { params, paramsSerializer: { indexes: null } });
    return response.data;
  }

  async getChat(chatId) {
    const response = await api.get(`${MESSENGER_BASE}/chats/${chatId}`);
    return response.data;
  }

  async markAsRead(chatId) {
    const response = await api.post(`${MESSENGER_BASE}/chats/${chatId}/read`);
    return response.data;
  }

  // ============================================
  // MENSAGENS
  // ============================================

  async getMessages(chatId, limit = 50, offset = 0, options = {}) {
    const response = await api.get(`${MESSENGER_BASE}/chats/${chatId}/messages`, {
      params: { limit, offset },
      signal: options.signal,
    });
    return response.data;
  }

  async sendMessage(chatId, message) {
    const response = await api.post(`${MESSENGER_BASE}/chats/${chatId}/messages`, {
      to: '',
      message,
    });
    return response.data;
  }

  /** media_type: 'image' | 'audio' | 'video' | 'file' (documento e aceito). */
  async sendMedia(toPsid, mediaUrl, mediaType) {
    const response = await api.post(`${MESSENGER_BASE}/send-media`, {
      to: toPsid,
      media_url: mediaUrl,
      media_type: mediaType,
    });
    return response.data;
  }

  // ============================================
  // UTILS
  // ============================================

  /** Nome de exibicao: so o nome do perfil (Messenger nao tem @handle).
   *
   * Antes do App Review a User Profile API da Meta nao devolve o nome de quem
   * escreve (exige Advanced Access ao pages_messaging), entao o chat pode
   * nascer com o PSID cru como nome. Nesse caso mostramos um rotulo amigavel
   * em vez do numero de 16-17 digitos; em producao (pos-review) o nome real
   * chega no perfil e substitui. */
  getChatDisplayName(chat) {
    if (!chat) return '';
    const name = chat.contact_name;
    if (!name || String(name) === String(chat.contact_psid)) {
      return 'Contato do Messenger';
    }
    return name;
  }

  getMessagePreview(chat) {
    if (!chat) return '';
    return chat.last_message_text || '';
  }

  /**
   * A janela de 24h esta aberta? Calculado no cliente a partir de
   * last_inbound_at para decidir se mostra o composer ou o aviso — o backend
   * revalida no envio, este calculo e so de UI.
   *
   * Usa a MESMA folga de 5 minutos do backend (grace, igual ao Instagram).
   * Sem ela havia uma faixa de 5 minutos em que a UI mostrava o composer
   * habilitado e o envio era recusado — o operador digitava e perdia o texto.
   */
  isWindowOpen(chat) {
    if (!chat || !chat.last_inbound_at) return false;
    const last = new Date(chat.last_inbound_at);
    if (isNaN(last.getTime())) return false;
    const janelaMs = 24 * 60 * 60 * 1000 - 5 * 60 * 1000;
    return Date.now() - last.getTime() < janelaMs;
  }

  formatTime(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';

    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

const messengerService = new MessengerService();
export default messengerService;
